import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, Compass, Copy, Download, Plus, Users } from 'lucide-react'
import { billingApi } from '@/lib/billingApi'
import { browserDownload } from '@/lib/desktop'
import { useAppStore } from '@/store/app'
import { cx } from '@/lib/cx'
import { MobileBack } from '@/components/MobileBack'
import { AddFriend } from './FriendsPage'
import chatStyles from '../ChatView.module.css'
import styles from './StartScreen.module.css'

/*
 * Первый экран того, у кого ещё нет ни одного сервера. Раньше он видел пустых «Друзей» —
 * тупик без следующего шага. Здесь три шага: свой сервер, позвать людей, поставить приложение
 * (только из браузера на Windows и macOS). Как только появится сервер, экран уступает место
 * обычным «Друзьям».
 */
export function StartScreen() {
  const openAddGuild = useAppStore((s) => s.setAddGuildOpen)
  const setView = useAppStore((s) => s.setView)
  const download = browserDownload()
  const refs = useQuery({ queryKey: ['referrals'], queryFn: billingApi.referrals, retry: 1, staleTime: 5 * 60_000 })
  const link = refs.data ? `${window.location.origin}/r/${refs.data.code}` : window.location.origin
  const [copied, setCopied] = useState(false)
  const copy = () => {
    void navigator.clipboard.writeText(link).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <main className={chatStyles.chat} aria-label="С чего начать">
      <header className={chatStyles.header}>
        <MobileBack label="К списку бесед" />
        <h1 className={chatStyles.title}>С чего начать</h1>
      </header>
      <div className={styles.body}>
        <p className={styles.lead}>Серверов пока нет. Создай свой сервер, загляни в каталог или пригласи друзей по ссылке.</p>
        <div className={styles.cards}>
          <section className={styles.card}>
            <Plus size={22} className={styles.icon} />
            <h2 className={styles.cardTitle}>Создать сервер</h2>
            <p className={styles.cardText}>Каналы, голос, роли. Бесплатно и сразу.</p>
            <div className={styles.foot}>
              <button type="button" className={styles.button} onClick={() => openAddGuild(true)}>Создать</button>
            </div>
          </section>
          <section className={styles.card}>
            <Compass size={22} className={styles.icon} />
            <h2 className={styles.cardTitle}>Каталог серверов</h2>
            <p className={styles.cardText}>Публичные сообщества. Вступить можно без приглашения.</p>
            <div className={styles.foot}>
              <button type="button" className={styles.button} onClick={() => setView({ kind: 'discovery' })}>Открыть каталог</button>
            </div>
          </section>
          <section className={styles.card}>
            <Users size={22} className={styles.icon} />
            <h2 className={styles.cardTitle}>Позвать друзей</h2>
            <p className={styles.cardText}>
              {refs.data ? `Приведите ${refs.data.need === 1 ? 'одного человека' : `${refs.data.need} человек`} — получите месяц Плюса.` : 'Ссылка на Кличат для друзей.'}
            </p>
            <div className={styles.foot}>
              <button type="button" className={cx(styles.button, copied && styles.buttonDone)} onClick={copy}>
                {copied ? <><Check size={16} /> Скопировано</> : <><Copy size={16} /> Скопировать</>}
              </button>
            </div>
          </section>
          {download ? (
            <section className={styles.card}>
              <Download size={22} className={styles.icon} />
              <h2 className={styles.cardTitle}>Скачать приложение</h2>
              <p className={styles.cardText}>Рация поверх игр, звук системы в трансляции, статус игры.</p>
              <div className={styles.foot}>
                <a className={styles.button} href={download.url}>{download.label}</a>
                {download.alt ? <a className={styles.alt} href={download.alt.url}>{download.alt.label}</a> : null}
              </div>
            </section>
          ) : null}
        </div>
        <div className={styles.friend}>
          <AddFriend />
        </div>
      </div>
    </main>
  )
}
